import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import Cookies from "js-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faArrowRight,
  faBatteryHalf,
  faExchangeAlt,
  faTrashAlt,
  faEye,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import "./AppTour.css";

/* -------------------------------------------------------------------------- */
/*                                 Tour Steps                                 */
/* -------------------------------------------------------------------------- */
const siteListSteps = [
  {
    title: (
      <div style={{ textAlign: "left" }}>
        <img
          style={{ marginBottom: "15px" }}
          src="/tehama_logo_100p.png"
          alt="Tehama Wireless Logo"
        />
        <span style={{ display: "block" }}>Let's begin our journey!</span>
      </div>
    ),
    content: "Welcome to the Tehama Wireless Site Manager.",
    placement: "center",
    target: "body",
    disableBeacon: true,
  },
  {
    target: ".site-add-button",
    title: "Add Site Button",
    content: "Click this to Add a Site using the Access Code.",
  },
  {
    target: ".site-icons",
    title: "Navigation Buttons",
    content: (
      <div>
        <p>
          <FontAwesomeIcon icon={faEdit} /> Click the Edit button to edit the
          Site Name or the Access Code.
        </p>

        <p>
          <FontAwesomeIcon icon={faArrowRight} /> Use the arrow button to open
          the Site.
        </p>
      </div>
    ),
  },
  {
    target: ".tour-help-link",
    title: "Help",
    content:
      "Click here to open a window explaining the various status Icons used on the site.",
  },
  {
    target: ".tour-dropdown",
    title: "Menu",
    content: "Main Menu available throughout the site. Go here for access to:",
    placement: "left",
  },
  {
    target: ".tour-menu-group1",
    placement: "left",
    content: (
      <div>
        <p>
          <b>My Account</b> to change your password.
        </p>
        <p>
          <b>Sites</b> to manage your SiteList (Add / Delete / Claim / Release).
        </p>
        <p>
          <b>Users</b> to manage your users (Add / Edit / Delete).
        </p>
      </div>
    ),
    styles: {
      options: {
        width: "490px",
      },
    },
  },
  {
    target: ".tour-menu-group2",
    placement: "left",
    // title: "Group 2",
    content: (
      <div>
        <p>
          <b>Battery Icons</b> is a toggle between showing Voltage or an battery
          Icon. <FontAwesomeIcon icon={faBatteryHalf} color="#e2ca00" />
        </p>
        <p>
          <b>Decimal Radio ID</b> is a toggle between Decimal or Hexadecimal
          listing of Radio IDs <b>(i.e., 4077914171 or F310083B).</b>
        </p>
        <p>
          <b>Other Resources</b> takes you to our main site and knowledgebase.
        </p>
      </div>
    ),
    styles: {
      options: {
        width: "490px",
      },
    },
  },
  {
    target: ".tour-sitehealth",
    title: "Site Health",
    content:
      "This shows the heath of your system based on DCAP check-ins. Click on a pie chart to filter the list to those not checking in for a period of time and may need to be checked for Power or Internet connection.",
    disableBeacon: true,
  },
];

const overviewSteps = [
  {
    disableBeacon: true,
    target: ".tour-overview-item1",
    title: "Navigation",
    placement: "right",
    content:
      "Use this area to navigate between the various screens available while connected to a Site.",
  },
  {
    target: ".site-form",
    title: "Site Info",
    content: (
      <p>
        Shows information about your Site’s location. Click <b>Edit</b> to
        update information about your Site.
      </p>
    ),
  },
  {
    target: ".reports-pane",
    title: "Daily Reports",
    content: (
      <p>
        Shows what report generator is used by the Site along with when and
        where the report is sent. Click <b>Edit</b> to update information about
        the report settings. To enter multiple email recipients, separate them
        with a comma (“,”).
      </p>
    ),
  },
  {
    target: ".display-form",
    title: "System Info",
    content: (
      <p>
        Shows information about the Tehama system at the site. This data is
        generated by the Tehama system and is not editable except to manage
        cellular service subscriptions.
      </p>
    ),
  },
  {
    target: ".device-health",
    title: "Site Health Area",
    content: (
      <div>
        <p>
          The <b>left circle</b> shows the number of MDTs commissioned at the
          site and the number of them that are reporting data.
        </p>
        <p>
          The <b>middle circle</b> shows how many have recorded utility usage in
          the last 24 hours.
        </p>
        <p>
          The <b>right circle</b> shows the number of Repeaters commissioned and
          reporting. 
        </p>
        <p>Clicking on any of these circles will take you to the
          Troubleshooting tab.
        </p>
      </div>
    ),
  },
];

const configSteps = [
  {
    disableBeacon: true,
    target: ".refresh-button-div",
    title: "Refresh",
    placement: "right",
    content:
      "Click Refresh to update the data for cases when a field tech may be making changes to the site while you are viewing it.",
  },
  {
    target: ".filter-item",
    title: "Filter",
    placement: "right",
    content:
      "This will filter the list for any text in the first five columns (Type through Apt).",
  },
  {
    target: ".button-row2",
    title: "Page Actions",
    placement: "left",
    content:
      "These four options allow you to Add a Device, Import/Upload or Download a CSV configuration file, or Mass Edit to change the settings (Building, Units or Utility) of all Checked devices at once.",
  },
  {
    target: ".tour-action-buttons",
    title: "Device Actions",
    placement: "left",
    content: (
      <div>
        <p>Allows individual updates to a Device</p>
        <p>
          The <FontAwesomeIcon icon={faEdit} /> <b>Edit icon</b> brings up the
          various screens to update Location or Meter Configuration data.
        </p>
        <p>
          The <FontAwesomeIcon icon={faExchangeAlt} /> <b>Arrows icon</b> lets
          you swap the Device associated with the apartment with a new Device.
        </p>
        <p>
          Finally, with the <FontAwesomeIcon icon={faTrashAlt} />{" "}
          <b>Trash icon</b> you can delete the row and the device from your
          configuration.
        </p>
      </div>
    ),
  },
];

const meteringSteps = [
  {
    disableBeacon: true,
    target: ".pcos",
    title: "Last Day",
    placement: "right",
    content:
      "A drop-down menu to select a range of time periods to monitor. The Custom option brings up a calendar to select specific start and end dates.",
  },
  {
    target: ".tour-metering-consumption",
    title: "Total Consumption",
    placement: "left",
    content:
      "Gives you a sum of all the utilities **over the time period selected**. ",
  },
  {
    target: ".tour-metering-pageactions",
    title: "Page Actions",
    placement: "left",
    content: (
      <div>
        <p>
          Click <b>Change View</b> to see a pie chart of usage per MDT, an easy
          way to discover the highest utility consumers.
        </p>
        <p>
          <b>Download CSV</b> generates a file of this grid of data which can be
          useful for example if you download a month of data; you can use the
          Consumption column for billing input for the month.
        </p>
      </div>
    ),
  },
  {
    target: ".tour-table-head",
    title: "Consumption and Reading",
    placement: "left",
    content: (
      <div>
        <p>
          The <b>Reading column</b> shows the most recent value while the{" "}
          <b>consumption column</b> shows the amount of the utility consumed
          **over the time period selected** and whose value **will** change as
          you select different time periods.
        </p>
        <p>
          <b>Reading 2</b> and <b>Consumption 2</b> columns are for our dual
          input MDTs; these columns will not be displayed if you only have
          single input MDTs at the site.
        </p>
      </div>
    ),
  },
  {
    target: ".tour-action-buttons",
    title: "Device Actions",
    placement: "left",
    content: (
      <div>
        <p>
          Click the <FontAwesomeIcon icon={faEye} /> <b>Eyeball</b> icon to get
          more detail about that device and to see the data in a timestamped
          grid.
        </p>
        <p>
          The <FontAwesomeIcon icon={faChartLine} /> <b>Graph</b> icon will plot
          the data. If you have dual input MDTs you will see each meter’s data
          in its own graph. A Change View button will toggle between the two
          once you are in one view.
        </p>
        <p>
          The <FontAwesomeIcon icon={faEdit} /> <b>Edit</b> icon brings up the
          various screens to update Location or Meter Configuration data.
        </p>
        <p>
          The <FontAwesomeIcon icon={faTrashAlt} /> <b>Trash</b> icon you can
          delete the row and the device from your configuration.
        </p>
      </div>
    ),
  },
];
const measureSteps = [
  {
    disableBeacon: true,
    target: ".control-row",
    content: (
      <p>
        The fixed top part of this page shows the kind of <b>Meter</b> in use. 
        The left navigation has tabs for all the different meters in use at any one site.
        Click those tabs to change meter types. Next to the meter type are counts showing
        the number of devices on the page and the total number of devices at this site. 
        Finally, on the right are two buttons, <b>Edit Selected</b> and <b>Edit All</b>
        . Both buttons open a dialog window to change settings. <b>Edit All</b> will apply
        changes to every device on the page. <b>Edit Selected</b> applies changes only to 
        devices that have checked boxes on the left hand side of the table. 
      </p>
    ),
  },
  {
    target: ".tour-table-head",
    content: (
      <p>
        <b>Meter properties</b> are always on the right hand side of the table. 
        However the specific properties change depending upon the meter type
        in use. Some meters have more editable properties, some have fewer. These can
        be seen most easily in the <b>Edit Selected</b> and <b>Edit All</b> dialogs. The
        table will show "Not Applicable" for properties that do not apply to the current
        meter type. 
      </p>
    ),
  },
]
const netstatSteps = [
  {
    disableBeacon: true,
    target: ".histogram-row",
    content: (
      <p>
        The main part of this page are the <b>three histograms</b> along the top. 
        Left to right these show MDT battery voltages, MDT Link Qualities, and 
        Repeater Link Qualities, with each bar representing the number of devices 
        at each level. A well-functioning site would show a single bar in the far 
        right column of each graph, meaning all the values are very good. Clicking 
        on any bar will filter the grid below to display only the devices in the 
        Histogram with a particular battery voltage or Link Quality range.
        </p>
    ),
  },
  {
    target: ".ts-status-row",
    content: (
      <p>
        <b>Below the graphs</b> are a few more statistics for the site: the
        number of MDTs reporting data **over the time period selected** and the
        number that shows utility usage. Clicking on those will also filter the
        grid data to those not reporting or advancing.
      </p>
    ),
  },
  {
    target: ".filter-item",
    title: "Filter",
    placement: "right",
    content:
      <span>
      <p>This will filter the list for any text in the first five columns (Radio Id to Battery).</p>
      <p>Additionally you can type MDT to see only MDTs, or RPTR to show just repeaters</p>
      </span>,
  },
 ]
  const devReadingSteps = [
    {
      disableBeacon: true,
      target: ".pcos",
      title: "Last Day",
      placement: "right",
      content:
        "A drop-down menu to select a range of time periods to monitor. The Custom option brings up a calendar to select specific start and end dates.",
    },
    {
      title: "Rollup",
      target: ".device-rollup",
      content: "Select None to see every reading for this period. Click on Daily to get one value for each calendar day",
    },
    {
      target: ".device-buttons",
      content: <p>Use change view to get a readings chart. The chart will use the current
        rollup, so a weekly chart with daily rollup has 7 points. Download CSV will
        create an excel file on your computer.   
      </p>
    },
  ]
  const notificationSteps = [
    {
      disableBeacon: true,
      target: ".csv-th",
      title: "Alert Emails",
      placement: "bottom",
      content:
        "Each email shown receives the notifications subscribed to on this page.",
    },
    {
      title: "All Notifications",
      target: "#k_all",
      content: "A shorthand way to subscribe to every supported notification type"
    },
    {
      title: "Low Voltage Notification",
      target: "#k_volt",
      content: <p><b>What: </b>An email whenever a MDT Low Battery Voltage or Solar Repeater Low 
        Power Voltage is detected. <br/>
        <b>From: </b>MDTs and Solar Repeaters<br/>
        <b>Details: </b><br/>● MDT Battery Low Voltage<br/>
          The MDT default minimum voltage value is 2.2 Volts with a 5% hysteresis.
          A 5% hysteresis value requires voltage reach 2.2 x 1.05 = 2.31V before 
          another notification is generated.<br/>
        ● Solar Repeater Low Power Voltage Alert<br/>
          The default minimum voltage value is 4.8 Volts with a 10% hysteresis.
      </p>  
    },
    {
      title: "Power Status Notification",
      target: "#k_power",
      content:<p><b>What: </b>An email when a repeater Power_Monitor sensor changes.<br/>
        <b>From: </b>Line powered repeaters<br/>
        <b>Details: </b>Alert Emails generate whenever power goes out or comes back on.
      </p>
    }, 
    {
      title: "Low Temp Notification",
      target: "#k_cold",
      content: <p><b>What: </b>Email when an MDT gets too cold.<br/>
        <b>From: </b>Any MDT<br/>
        <b>Details: </b>By default, the minimum notification temperature is 36 degrees Fahrenheit.
         We also support a hysteresis value. This is the temperature rise required before another 
         notification is generated. The default 10 degree hysteresis means that after a 
         notification, temperature must rise to 46°F before another notification is generated.
        </p>
    },
        {
      title: "High Temp Notification",
      target: "#k_hot",
      content: <p><b>What: </b>An email when an MDT temperature rises above a maximum threshold.<br/>
        <b>From: </b>Any MDT<br/>
        <b>Details: </b>By default, the maximum threshold is 100 degrees Fahrenheit. We also support a hysteresis value. This is the temperature decrease
        required before another notification is generated. 
        The default 10 degree hysteresis means that after a notification, temperature must 
        drop to 90°F before another notification is generated.
        </p>
    },
    {
      title: "Standing Water Notification",
      target: "#k_water",
      content: <p><b>What: </b>An email whenever pools of water are detected. <br/>
        <b>From: </b>Standing Water MDTs (MDTs with a water sensing cable)<br/>
        <b>Details: </b>Sent when the status of a Standing Water Leak Detector sensor changes. 
          Two parameters can be configured. They are messages to be sent when water is present and when it has cleared.
          The defaults are “Leak!” and “NO Leak” but can be changed to something more specific
          such as “Leak in bathroom!” or “No Leak in bathroom” 
        </p>
    },
    {
      title: "Continuous Flow Leak Notification",
      target: "#k_flow",
      content: <p><b>What: </b>An email whenever the continuous flow of water is detected. <br/>
        <b>From: </b>Any configured MDT<br/>
        <b>Details: </b>Generated after each reading is greater than the previous for a number of hours.
          The default is 24 hours for all meter types except Encoded meters where it is 72.
        </p>
    },
    {
      title: "DCAP Down Notification",
      target: "#k_down",
      content: <p><b>What: </b>An email whenever a site's DCAP has not forwarded a message
        to the Cloud for some time.<br/>
        <b>From: </b>DCAPs<br/>
        <b>Details: </b> You can configure the time to 24 hours, 48 hours, 
        2 weeks or all three. <br/><br/>
        Note that some sites can receive data even after a DCAP stops communicating. 
        It requires a neighboring DCAP to handle MDT data. 
        </p>
    },
  ]



/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                             App tour component                             */
/* -------------------------------------------------------------------------- */
export default function AppTour(props) {
  const {
    tour,
    updateDropmenuState,
    launchTourStatus,
    siteLocation,
    location,
  } = props;

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourSteps, setTourSteps] = useState();

  useEffect(() => {
    //Tour steps map
    const tourStepsMap = {
      chgsite: siteListSteps,
      overview: overviewSteps,
      config: configSteps,
      metering: meteringSteps,
      netstat: netstatSteps,
      measure: measureSteps,
      tourDevReadings: devReadingSteps,
      alertset: notificationSteps,

      /*If you want to add more tours, you can add them here and create the object above
      as the object above */
      // commission: commissionSteps,
      // netmap: netmapSteps,
      // alertset: alertsetSteps,
    };

    //Check if tour is enabled
    if (tour) {
      //Check if the tour has been run before on the sitelist page
      if (Cookies.get("tour-" + siteLocation) !== "ran") {
        //check if the tour has been run before on the other pages
        const selectedSteps = tourStepsMap[siteLocation];
        if (selectedSteps) {
          setTourSteps(selectedSteps);
          setStepIndex(0);
          setTimeout(() => {
            setRun(true);
          }, 500);
        }
      } else {
        //If the tour has been run before, stop the tour
        setRun(false);
        launchTourStatus(false);
      }
    } else {
      //If the tour is not enabled, stop the tour
      setRun(false);
    }
  }, [tour, siteLocation, location, launchTourStatus]);

  const optionalSkipStep = (nextStepIndex, action) => {
    let found = true
    do {
      const nextTarget = notificationSteps[nextStepIndex]?.target
      found = true
      if (nextTarget && nextTarget.startsWith('#')) { // conditionally present targets
        const element = document.getElementById(nextTarget.slice(1))
        if (!element) {
          nextStepIndex += (action === "prev" ? -1 : 1)
          found = false
        }
      }
    } while (!found && nextStepIndex < notificationSteps.length)
    return nextStepIndex
  }

  //Callback function for the Tour
  const handleJoyrideCallback = (data) => {
    const { index, action, status, type } = data;
    let nextStepIndex = Math.max(index + (action === "prev" ? -1 : 1), 0)

    //If the user clicks next, update the step index
    if (action === "next" && type === "step:after" && tour) {
      setRun(false);
      if (siteLocation === 'alertset')
        nextStepIndex = optionalSkipStep (nextStepIndex, action)
      setStepIndex(nextStepIndex);

      setTimeout(() => {
        setRun(true);
      }, 100);
    }

    //Check if the user is on the sitelist and is the step where the menu should be open
    if ((nextStepIndex === 5 || nextStepIndex === 6 || nextStepIndex === 7) && siteLocation === "chgsite") {
      updateDropmenuState(true);
    }

    //Check if the user is on the sitelist and is the step where the menu should be closed
    if (nextStepIndex === 8 && siteLocation === "chgsite") {
      updateDropmenuState(false);
    }

    //If the tour is finished, stop the tour and set the cookie
    if (status === "finished") {
      launchTourStatus(false);
      setRun(false);
      Cookies.set("tour-" + siteLocation, "ran", { expires: 3650 });
    }

    //If the user closes the tour, stop the tour and set the cookie
    if (action === "close") {
      launchTourStatus(false);
      setRun(false);
      Cookies.set("tour-" + siteLocation, "ran",  { expires: 3650 });
    }
  };

  return (
    <div>
      <Joyride
        stepIndex={stepIndex}
        run={run}
        steps={tourSteps}
        callback={handleJoyrideCallback}
        hideBackButton
        continuous
        scrollToFirstStep
        showProgress
        disableScrolling
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: "#00c0a8",
            backgroundColor: "#FCFCFC",
          },
          tooltipContent: {
            padding: "0px",
            textAlign: "left",
            fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
          },
          tooltipTitle: {
            fontSize: 24,
            marginBottom: "5px",
            textAlign: "left",
            fontFamily: 'Montserrat, "Helvetica Neue", sans-serif',
          },
          tooltipContainer: {
            padding: "15px 10px",
          },
        }}
      />
    </div>
  );
}
